import { ApiResponse } from '@/types';
import api from './client';
import { requireUUID } from './contractValidation';

// Match filter option for event analysis
export interface MatchFilterOption {
  matchId: number;
  label: string;
  dateUtc: string;
  position: string;
  venue: string;
  competitionId: number;
}

// Player Event Analysis types
export interface PlayerEventAnalysis {
  matchCount: number;
  totalEvents: number;
  passing: {
    total: number;
    accurate: number;
    accuracyPct: number;
    byDirection: Record<string, { count: number; accurate: number }>;
    keyPasses: number;
    progressivePasses: number;
    crosses: number;
    crossesAccurate: number;
    longPasses: number;
    longAccurate: number;
    throughBalls: number;
    avgLength: number;
    zones: Record<string, { count: number; accurate: number; accuracyPct: number }>;
  };
  shooting: {
    total: number;
    onTarget: number;
    goals: number;
    accuracyPct: number;
    conversionPct: number;
    totalXg: number;
    avgXgPerShot: number;
    byBodyPart: Record<string, { shots: number; goals: number; xg: number }>;
    zones: Record<string, { shots: number; goals: number; xg: number }>;
  };
  defensive: {
    groundDuels: { total: number; won: number; winRate: number };
    aerialDuels: { total: number; won: number; winRate: number };
    interceptions: number;
    clearances: number;
    fouls: number;
    foulsSuffered: number;
    yellowCards: number;
    redCards: number;
    recoveries: { total: number; defThird: number; midThird: number; attThird: number };
    zones: Record<string, { total: number; duels: number; duelsWon: number; interceptions: number; clearances: number; recoveries: number }>;
  };
  activityByPeriod: Record<string, { total: number; passes: number; shots: number; duels: number }>;
}

// Build-Up Play Analysis types
export interface BuildUpPlayer {
  id: number;
  name: string;
  position?: string;
}

export interface BuildUpLocation {
  x: number;
  y: number;
}

export interface SequenceEvent {
  minute: number;
  second: number;
  player?: BuildUpPlayer;
  action: string;
  actionDetail?: string;
  location?: BuildUpLocation;
  endLocation?: BuildUpLocation;
  accurate?: boolean;
}

export interface GoalBuildUp {
  minute: number;
  period: string;
  bodyPart?: string;
  flank?: string;
  startZone?: string;
  duration?: string;
  patternSummary: string;
  scorer?: BuildUpPlayer;
  xg: number;
  sequence: SequenceEvent[];
  sequenceLength: number;
  isOwnGoal?: boolean;
  setPieceType?: string;
  leadUp?: SequenceEvent[];
}

export interface MatchBuildUpAnalysis {
  ourTeamId: number;
  ourTeamName?: string;
  goalsScored: GoalBuildUp[];
  goalsConceded: GoalBuildUp[];
}

export interface PatternGoalRef {
  matchId: number;
  matchLabel: string;
  minute: number;
  scorer?: string;
}

export interface BuildUpPattern {
  pattern: string;
  frequency: number;
  goals: PatternGoalRef[];
  avgSequenceLen: number;
  commonStartZone?: string;
  commonFlank?: string;
}

export interface AggregateBuildUpStats {
  avgSeqLen: number;
  commonStartZones: Record<string, number>;
  commonFlanks: Record<string, number>;
}

export interface AggregateBuildUpAnalysis {
  matchesAnalyzed: number;
  totalGoalsScored: number;
  totalGoalsConceded: number;
  scoredPatterns: BuildUpPattern[];
  concededPatterns: BuildUpPattern[];
  scoredStats: AggregateBuildUpStats;
  concededStats: AggregateBuildUpStats;
}

// AI Chat types
export interface AIChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AIChatSession {
  id: string;
  entityType: 'match' | 'player' | 'league_match';
  entityId: string;
  entityLabel?: string;
  profileId?: string | null;
  profileGrounded?: boolean;
  title: string;
  createdBy: string;
  creatorName: string;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AIChatMessageRecord {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant';
  content: string;
  userId?: string;
  userName?: string;
  createdAt: string;
}

export interface WyscoutPlayerFixture {
  matchId: number;
  detailsAvailable: boolean;
  dateUtc: string;
  label: string | null;
  playerTeamId: number | null;
  playerTeamName: string | null;
  opponentTeamId: number | null;
  opponentTeamName: string | null;
  competitionId: number;
  competitionName: string | null;
  seasonId: number;
  seasonName: string | null;
  venue: 'home' | 'away' | 'neutral' | null;
  playerScore: number | null;
  opponentScore: number | null;
  result: 'win' | 'draw' | 'loss' | null;
  participationStatus: 'played' | 'did_not_play';
  statsStatus: 'available' | 'unavailable' | 'failed' | 'ignored';
  excluded: boolean;
  appearances: number | null;
  minutes: number | null;
  goals: number | null;
  assists: number | null;
  shots: number | null;
  shotsOnTarget: number | null;
  xg: number | null;
  xgAssist: number | null;
  passes: number | null;
  successfulPasses: number | null;
  duels: number | null;
  duelsWon: number | null;
  defensiveActions: number | null;
  interceptions: number | null;
  recoveries: number | null;
  yellowCards: number | null;
  redCards: number | null;
  position: string | null;
  failureCode: string | null;
  failureMessage: string | null;
}

export interface WyscoutPlayerFixtureFilterOption {
  id: number;
  name: string | null;
}

export interface WyscoutPlayerFixtureFilterOptions {
  clubs: WyscoutPlayerFixtureFilterOption[];
  competitions: WyscoutPlayerFixtureFilterOption[];
  seasons: WyscoutPlayerFixtureFilterOption[];
  positions: string[];
  earliestDate: string | null;
  latestDate: string | null;
}

export interface WyscoutPlayerFixtureCoverage {
  totalFixtures: number;
  playedFixtures: number;
  didNotPlayFixtures: number;
  availableStats: number;
  unavailableStats: number;
  nonAppearanceComplete: boolean;
  guidance: string;
}

// Wyscout Matches API
export const wyscoutMatchesAPI = {
  // Get the configured team ID
  getTeamInfo: async (): Promise<ApiResponse<{ teamId: number }>> => {
    const response = await api.get('/wyscout/team-info');
    return response.data;
  },

  // Get stored matches from database
  getStoredMatches: async (): Promise<ApiResponse<Array<{
    id: number;
    wyscoutMatchId: number;
    dateUtc: string;
    status: string;
    venue: string;
    label: string;
    homeTeamId: number;
    homeScore: number;
    awayTeamId: number;
    awayScore: number;
    competitionId: number;
    seasonId: number;
  }>>> => {
    const response = await api.get('/wyscout/stored-matches');
    return response.data;
  },

  // Get detailed match data
  getStoredMatch: async (matchId: number): Promise<ApiResponse<{
    id: number;
    wyscoutMatchId: number;
    competitionId: number;
    seasonId: number;
    roundId: number;
    gameweek: number;
    dateLocal: string;
    dateUtc: string;
    status: string;
    duration: string;
    winner: number;
    venue: string;
    label: string;
    hasDataAvailable: boolean;
    homeTeamId: number;
    homeCoachId: number;
    homeScore: number;
    homeScoreHt: number;
    homeScoreEt: number;
    homeScoreP: number;
    homeHasFormation: boolean;
    homeFormationSchema: string;
    homeLineup: Array<{ playerId: number; shirtNumber: number; position: string }>;
    homeBench: Array<{ playerId: number; shirtNumber: number; position: string }>;
    homeSubstitutions: Array<{ playerIn: number; playerOut: number; minute: number }>;
    awayTeamId: number;
    awayCoachId: number;
    awayScore: number;
    awayScoreHt: number;
    awayScoreEt: number;
    awayScoreP: number;
    awayHasFormation: boolean;
    awayFormationSchema: string;
    awayLineup: Array<{ playerId: number; shirtNumber: number; position: string }>;
    awayBench: Array<{ playerId: number; shirtNumber: number; position: string }>;
    awaySubstitutions: Array<{ playerIn: number; playerOut: number; minute: number }>;
    referees: Array<{ refereeId: number; role: string }>;
  }>> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}`);
    return response.data;
  },

  // Refresh matches from Wyscout API
  refreshMatches: async (seasonId?: number): Promise<{
    data: Array<{
      id: number;
      wyscoutMatchId: number;
      dateUtc: string;
      status: string;
      venue: string;
      label: string;
      homeTeamId: number;
      homeScore: number;
      awayTeamId: number;
      awayScore: number;
      competitionId: number;
      seasonId: number;
    }>;
    meta: {
      savedCount: number;
      totalMatches: number;
    };
  }> => {
    const params = seasonId ? `?seasonId=${seasonId}` : '';
    const response = await api.post(`/wyscout/refresh-matches${params}`);
    return response.data;
  },

  // Get advanced stats for a match (checks DB first, then fetches from API if needed)
  getMatchAdvancedStats: async (matchId: number): Promise<{
    data: {
      matchId: number;
      general: Record<string, {
        shots: number;
        shotsOnTarget: number;
        goals: number;
        xg: number;
        corners: number;
        fouls: number;
        yellowCards: number;
        redCards: number;
        offsides: number;
        freeKicks: number;
        progressiveRuns: number;
        touchesInBox: number;
      }>;
      possession: {
        totalTime: string;
        deadTime: string;
        [teamId: string]: {
          possessionPercent: number;
          purePossessionTime: string;
          possessionNumber: number;
          reachingOpponentHalf: number;
          reachingOpponentBox: number;
        } | string;
      };
      passes: Record<string, {
        passes: number;
        passesSuccessful: number;
        crossesTotal: number;
        crossesSuccessful: number;
        longPasses: number;
        longPassesSuccessful: number;
        keyPasses: number;
        assists: number;
        avgPassLength: number;
      }>;
      defence: Record<string, {
        tackles: number;
        interceptions: number;
        clearances: number;
        ppda: number;
      }>;
      duels: Record<string, {
        duels: number;
        duelsSuccessful: number;
        aerialDuels: number;
        aerialDuelsSuccessful: number;
        dribbles: number;
        dribblesSuccessful: number;
      }>;
      attacks: Record<string, {
        total: number;
        withShots: number;
        positionalAttack: number;
        counterAttacks: number;
        corners: number;
      }>;
      flanks: Record<string, {
        leftFlankAttacks: number;
        leftFlankXg: number;
        rightFlankAttacks: number;
        rightFlankXg: number;
        centerAttacks: number;
        centerXg: number;
      }>;
    };
    source: 'database' | 'api';
  }> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}/advanced-stats`);
    return response.data;
  },

  // Get match events (goals, shots, cards, fouls, etc.) - checks DB first, then API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMatchEvents: async (matchId: number): Promise<Record<string, any>> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}/events`);
    return response.data;
  },

  // Batch resolve Wyscout players to internal player records
  resolvePlayers: async (players: Array<{ wyscoutPlayerId: number; name: string; shortName: string; position: string }>): Promise<{
    data: Record<string, { playerId: string; action: 'existing' | 'linked' | 'created'; name?: string }>;
  }> => {
    const response = await api.post('/wyscout/players/resolve', { players });
    return response.data;
  },

  // Get aggregated Wyscout stats for an internal player
  getPlayerWyscoutStats: async (playerId: string): Promise<{
    data: {
      matchesPlayed: number;
      totals: {
        matchesPlayed: number;
        minutes: number;
        goals: number;
        assists: number;
        xg: number;
        xgAssist: number;
        shots: number;
        shotsOnTarget: number;
        passes: number;
        successfulPasses: number;
        keyPasses: number;
        duels: number;
        duelsWon: number;
        interceptions: number;
        clearances: number;
        recoveries: number;
        losses: number;
        dribbles: number;
        successfulDribbles: number;
        yellowCards: number;
        redCards: number;
        fouls: number;
        progressiveRun: number;
        touchInBox: number;
        progressivePasses: number;
      };
      perMatch: Array<{
        matchId: number;
        position: string;
        minutes: number;
        goals: number;
        assists: number;
        xg: number;
        shots: number;
        passes: number;
        successfulPasses: number;
        keyPasses: number;
        duels: number;
        duelsWon: number;
        interceptions: number;
        recoveries: number;
        yellowCards: number;
        redCards: number;
        label: string;
        dateUtc: string;
      }>;
      fixtures: WyscoutPlayerFixture[];
      filterOptions: WyscoutPlayerFixtureFilterOptions;
      coverage: WyscoutPlayerFixtureCoverage;
    };
  }> => {
    const response = await api.get(`/wyscout/players/${playerId}/stats`);
    return response.data;
  },

  // Get aggregated event analysis for a player
  getPlayerEventAnalysis: async (playerId: string, filters?: {
    matchIds?: number[];
    positions?: string[];
    venues?: string[];
  }): Promise<{ data: PlayerEventAnalysis; filterOptions: MatchFilterOption[] }> => {
    const params = new URLSearchParams();
    if (filters?.matchIds?.length) params.append('matchIds', filters.matchIds.join(','));
    if (filters?.positions?.length) params.append('positions', filters.positions.join(','));
    if (filters?.venues?.length) params.append('venues', filters.venues.join(','));
    const qs = params.toString();
    const response = await api.get(`/wyscout/players/${playerId}/event-analysis${qs ? `?${qs}` : ''}`, { timeout: 60000 });
    return response.data;
  },

  // Get goal build-up analysis for a match
  getMatchBuildUpPlays: async (matchId: number): Promise<{ data: MatchBuildUpAnalysis }> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}/build-up-plays`, { timeout: 60000 });
    return response.data;
  },

  // Get aggregate goal build-up analysis across all matches
  getAggregateBuildUpAnalysis: async (): Promise<{ data: AggregateBuildUpAnalysis }> => {
    const response = await api.get('/wyscout/build-up-analysis', { timeout: 60000 });
    return response.data;
  },

  // Send AI chat message for a match
  sendMatchChat: async (matchId: number, messages: AIChatMessage[], preset?: string, focusPlayerId?: number, sessionId?: string): Promise<{
    data: { reply: string; sessionId: string };
  }> => {
    const response = await api.post(`/wyscout/stored-matches/${matchId}/ai-chat`, {
      messages,
      preset: preset || undefined,
      playerId: focusPlayerId || undefined,
      sessionId: sessionId || undefined,
    }, { timeout: 60000 });
    return response.data;
  },

  // Send AI chat message for a player
  sendPlayerChat: async (playerId: string, messages: AIChatMessage[], preset?: string, sessionId?: string, profileId?: string): Promise<{
    data: { reply: string; sessionId: string };
  }> => {
    if (profileId !== undefined) requireUUID(profileId, 'Invalid profile ID');
    const response = await api.post(`/wyscout/players/${playerId}/ai-chat`, {
      messages,
      preset: preset || undefined,
      sessionId: sessionId || undefined,
      ...(profileId === undefined ? {} : { profileId }),
    }, { timeout: 60000 });
    return response.data;
  },

  // List all chat sessions for the current user
  listMyChatSessions: async (): Promise<{ data: AIChatSession[] }> => {
    const response = await api.get('/wyscout/ai-chat/sessions');
    return response.data;
  },

  // List saved chat sessions for a match
  listMatchChatSessions: async (matchId: number): Promise<{ data: AIChatSession[] }> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}/ai-chat/sessions`);
    return response.data;
  },

  // List saved chat sessions for a player
  listPlayerChatSessions: async (playerId: string): Promise<{ data: AIChatSession[] }> => {
    const response = await api.get(`/wyscout/players/${playerId}/ai-chat/sessions`);
    return response.data;
  },

  // Get all messages for a chat session
  getChatSessionMessages: async (sessionId: string): Promise<{ data: AIChatMessageRecord[] }> => {
    const response = await api.get(`/wyscout/ai-chat/sessions/${sessionId}/messages`);
    return response.data;
  },

  // Delete a chat session
  deleteChatSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/wyscout/ai-chat/sessions/${sessionId}`);
  },

  // Get player advanced stats for a match (checks DB first, then fetches from API if needed)
  getMatchPlayerStats: async (matchId: number): Promise<{
    data: Array<{
      playerId: number;
      name: string;
      shortName: string;
      position: string;
      teamId?: number;
      stats: {
        playerId: number;
        matchId: number;
        teamId: number;
        player?: {
          currentTeamId: number;
          firstName?: string;
          lastName?: string;
          shortName?: string;
        };
        total: {
          goals: number;
          assists: number;
          shots: number;
          shotsOnTarget: number;
          minutesOnField: number;
          passes: number;
          successfulPasses: number;
          keyPasses: number;
          crosses: number;
          successfulCrosses: number;
          duels: number;
          duelsWon: number;
          aerialDuels: number;
          aerialDuelsWon: number;
          dribbles: number;
          successfulDribbles: number;
          interceptions: number;
          tackles: number;
          clearances: number;
          fouls: number;
          foulsSuffered: number;
          yellowCards: number;
          redCards: number;
          xgShot: number;
          xgAssist: number;
          progressiveRun: number;
          touchInBox: number;
          recoveries: number;
          losses: number;
        };
        average: Record<string, number>;
        percent: Record<string, number>;
      };
    }>;
    source: 'database' | 'api';
    count: number;
  }> => {
    const response = await api.get(`/wyscout/stored-matches/${matchId}/player-stats`);
    return response.data;
  },
};
